"use client"

import { useMemo, useCallback } from "react"
import { hierarchy, tree } from "d3-hierarchy"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { TreeNode, TreeStep } from "./types"

// ── Props ──

interface ExtraArray {
  label: string
  values: (number | string)[]
}

interface TreeVizProps {
  initialTree: TreeNode
  steps: TreeStep[]
  extraArrays?: ExtraArray[]
  width?: number
  height?: number
}

// ── Internal node type carrying visual state ──

interface InternalNode {
  id: number
  value: number | string
  children?: InternalNode[]
  color?: string
  highlighted?: boolean
  isNew?: boolean
}

// ── Deep clone helper ──

function cloneTree(node: InternalNode): InternalNode {
  return {
    ...node,
    children: node.children?.map(cloneTree),
  }
}

// ── Find a node by id ──

function findNode(root: InternalNode, id: number): InternalNode | null {
  if (root.id === id) return root
  if (!root.children) return null
  for (const child of root.children) {
    const found = findNode(child, id)
    if (found) return found
  }
  return null
}

// ── Find parent of a node by id ──

function findParent(
  root: InternalNode,
  id: number
): { parent: InternalNode; index: number } | null {
  if (!root.children) return null
  for (let i = 0; i < root.children.length; i++) {
    if (root.children[i]!.id === id) return { parent: root, index: i }
    const found = findParent(root.children[i]!, id)
    if (found) return found
  }
  return null
}

// ── Clear all highlights recursively ──

function clearHighlights(node: InternalNode): void {
  node.highlighted = false
  node.isNew = false
  node.color = undefined
  node.children?.forEach(clearHighlights)
}

// ── Clear only the highlighted flag (not color) ──

function clearHighlightFlags(node: InternalNode): void {
  node.highlighted = false
  node.isNew = false
  node.children?.forEach(clearHighlightFlags)
}

// ── Apply a single step to a mutable tree ──

function applyStep(root: InternalNode, step: TreeStep): void {
  switch (step.type) {
    case "compare": {
      if (step.nodeId != null) {
        const node = findNode(root, step.nodeId)
        if (node) node.highlighted = true
      }
      break
    }

    case "place": {
      if (step.parentId == null || step.val == null || step.nodeId == null) break
      const parent = findNode(root, step.parentId)
      if (!parent) break
      if (!parent.children) parent.children = []

      const newNode: InternalNode = {
        id: step.nodeId,
        value: step.val,
        color: step.color,
        isNew: true,
      }

      if (step.side === "left") {
        parent.children.unshift(newNode)
      } else {
        parent.children.push(newNode)
      }
      break
    }

    case "delete": {
      if (step.nodeId == null) break
      const result = findParent(root, step.nodeId)
      if (result) {
        result.parent.children!.splice(result.index, 1)
        if (result.parent.children!.length === 0) {
          result.parent.children = undefined
        }
      }
      break
    }

    case "highlight": {
      if (step.nodeId != null) {
        const node = findNode(root, step.nodeId)
        if (node) {
          node.highlighted = true
          if (step.color) node.color = step.color
        }
      }
      break
    }

    case "rotate": {
      if (step.nodeId == null) break
      const pivot = findNode(root, step.nodeId)
      if (!pivot || !pivot.children) break
      const parentResult = findParent(root, step.nodeId)

      if (step.rotationType === "LL" || step.rotationType === "LR") {
        // Right rotation: left child becomes new root of subtree
        const leftChild = pivot.children[0]
        if (!leftChild) break
        pivot.children[0] = leftChild.children?.[1] ?? (undefined as never)
        pivot.children = pivot.children.filter(Boolean)
        if (!leftChild.children) leftChild.children = []
        leftChild.children[1] = { ...pivot, children: pivot.children?.length ? [...pivot.children] : undefined }
        if (parentResult) {
          parentResult.parent.children![parentResult.index] = leftChild
        }
      } else {
        // Left rotation (RR, RL): right child becomes new root of subtree
        const rightChild = pivot.children[pivot.children.length - 1]
        if (!rightChild) break
        pivot.children[pivot.children.length - 1] = rightChild.children?.[0] ?? (undefined as never)
        pivot.children = pivot.children.filter(Boolean)
        if (!rightChild.children) rightChild.children = []
        rightChild.children[0] = { ...pivot, children: pivot.children?.length ? [...pivot.children] : undefined }
        if (parentResult) {
          parentResult.parent.children![parentResult.index] = rightChild
        }
      }
      break
    }

    case "reset": {
      clearHighlights(root)
      break
    }
  }
}

// ── Build the tree state at a given step index ──

function buildTreeAtStep(
  initialTree: TreeNode,
  steps: TreeStep[],
  targetIndex: number
): InternalNode {
  const root = cloneTree(initialTree as InternalNode)
  clearHighlightFlags(root)

  for (let i = 0; i <= targetIndex; i++) {
    applyStep(root, steps[i]!)
  }

  return root
}

// ── D3 layout types ──

interface PositionedNode {
  id: number
  value: number | string
  x: number
  y: number
  color?: string
  highlighted?: boolean
  isNew?: boolean
  parentX?: number
  parentY?: number
}

// ── Component ──

const CELL_SIZE = 40
const CELL_GAP = 6

export function TreeViz({
  initialTree,
  steps,
  extraArrays = [],
  width = 600,
  height: heightProp = 400,
}: TreeVizProps) {
  const extraRowHeight = extraArrays.length > 0 ? 70 : 0
  const height = heightProp + extraRowHeight
  const playback = usePlayback(steps.length)

  // Build current tree state based on stepIndex
  const currentTree = useMemo(
    () =>
      playback.stepIndex < 0
        ? cloneTree(initialTree as InternalNode)
        : buildTreeAtStep(initialTree, steps, playback.stepIndex),
    [initialTree, steps, playback.stepIndex]
  )

  // Compute layout positions using d3-hierarchy
  const { nodes, links } = useMemo(() => {
    const treeLayout = tree<InternalNode>().size([width - 80, height - 80])
    const root = hierarchy(currentTree, (d) => d.children ?? [])
    treeLayout(root)

    const positioned: PositionedNode[] = []
    const edgeLinks: { source: PositionedNode; target: PositionedNode }[] = []

    root.each((d) => {
      const node: PositionedNode = {
        id: d.data.id,
        value: d.data.value,
        x: d.x!,
        y: d.y!,
        color: d.data.color,
        highlighted: d.data.highlighted,
        isNew: d.data.isNew,
      }
      if (d.parent) {
        node.parentX = d.parent.x!
        node.parentY = d.parent.y!
      }
      positioned.push(node)
    })

    root.links().forEach((link) => {
      const source = positioned.find((n) => n.id === link.source.data.id)
      const target = positioned.find((n) => n.id === link.target.data.id)
      if (source && target) {
        edgeLinks.push({ source, target })
      }
    })

    return { nodes: positioned, links: edgeLinks }
  }, [currentTree, width, height])

  // Bezier path for a parent-child link
  const linkPath = useCallback(
    (link: { source: PositionedNode; target: PositionedNode }) => {
      const sx = link.source.x
      const sy = link.source.y
      const tx = link.target.x
      const ty = link.target.y
      const midY = (sy + ty) / 2
      return `M ${sx},${sy} C ${sx},${midY} ${tx},${midY} ${tx},${ty}`
    },
    []
  )

  const currentStep =
    playback.stepIndex >= 0 && playback.stepIndex < steps.length
      ? steps[playback.stepIndex]
      : null

  return (
    <div className="flex flex-col gap-3">
      <svg
        width={width}
        height={height}
        className="rounded-lg border border-border bg-background"
      >
        <g transform="translate(40, 40)">
          {/* Links */}
          {links.map((link) => (
            <path
              key={`link-${link.source.id}-${link.target.id}`}
              d={linkPath(link)}
              fill="none"
              stroke="var(--color-border)"
              strokeWidth={1.5}
              style={{ transition: "all 500ms ease" }}
            />
          ))}

          {/* Nodes */}
          {nodes.map((node) => (
            <g
              key={`node-${node.id}`}
              transform={`translate(${node.x}, ${node.y})`}
              style={{
                transition: "all 500ms ease",
                opacity: node.isNew ? 0 : 1,
                animation: node.isNew ? "fadeIn 500ms ease forwards" : undefined,
              }}
            >
              {/* Highlight ring */}
              {node.highlighted && (
                <circle
                  r={24}
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth={3}
                  strokeOpacity={0.6}
                  style={{ transition: "all 500ms ease" }}
                />
              )}

              {/* Node circle */}
              <circle
                r={20}
                fill={node.color ?? "var(--color-card)"}
                stroke={
                  node.highlighted
                    ? "var(--color-primary)"
                    : "var(--color-border)"
                }
                strokeWidth={node.highlighted ? 2.5 : 1.5}
                style={{ transition: "all 500ms ease" }}
              />

              {/* Label */}
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={13}
                fontWeight={500}
                fill="var(--color-foreground)"
                style={{
                  pointerEvents: "none",
                  userSelect: "none",
                  transition: "fill 500ms ease",
                }}
              >
                {String(node.value)}
              </text>
            </g>
          ))}
        </g>

        {/* Extra arrays (e.g., heap backing array, BFS queue) */}
        {extraArrays.map((extra, rowIdx) => {
          const cellW = CELL_SIZE + CELL_GAP
          const totalW = extra.values.length * CELL_SIZE + (extra.values.length - 1) * CELL_GAP
          const ox = (width - totalW) / 2
          const oy = heightProp - 10 + rowIdx * 60
          return (
            <g key={`extra-${rowIdx}`} transform={`translate(${ox}, ${oy})`}>
              <text
                x={-8}
                y={CELL_SIZE / 2}
                textAnchor="end"
                dominantBaseline="central"
                fontSize={11}
                fill="var(--color-muted-foreground)"
              >
                {extra.label}
              </text>
              {extra.values.map((val, i) => (
                <g key={i}>
                  <rect
                    x={i * cellW}
                    y={0}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    rx={4}
                    fill="var(--color-muted)"
                    stroke="var(--color-border)"
                    strokeWidth={1}
                  />
                  <text
                    x={i * cellW + CELL_SIZE / 2}
                    y={CELL_SIZE / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={12}
                    fontFamily="ui-monospace, monospace"
                    fill="var(--color-foreground)"
                  >
                    {val}
                  </text>
                  <text
                    x={i * cellW + CELL_SIZE / 2}
                    y={CELL_SIZE + 12}
                    textAnchor="middle"
                    fontSize={9}
                    fill="var(--color-muted-foreground)"
                  >
                    {i}
                  </text>
                </g>
              ))}
            </g>
          )
        })}

        {/* Inline keyframe for fade-in animation */}
        <defs>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
        </defs>
      </svg>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={playback.isPlaying}
        atStart={playback.atStart}
        atEnd={playback.atEnd}
        stepIndex={playback.stepIndex}
        totalSteps={steps.length}
        onStepBack={playback.stepBack}
        onStepForward={playback.stepForward}
        onPlay={playback.play}
        onPause={playback.pause}
        onReset={playback.reset}
      />
    </div>
  )
}
